import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError, api, setCsrfToken, type Identity } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Operator/admin sign-in (D33). Homeowners never see this — they use magic links.
 * The failure message is whatever the API says, which is deliberately the same for a wrong
 * password and an unknown username (08 §1).
 */
export function StaffLogin() {
  const navigate = useNavigate()
  const load = useSession((state) => state.load)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const form = new FormData(event.currentTarget)
    try {
      const identity = await api.post<Identity>('/auth/staff/login', {
        username: String(form.get('username') ?? ''),
        password: String(form.get('password') ?? ''),
        totp: String(form.get('totp') ?? '') || null,
        remember: form.get('remember') === 'on',
      })
      setCsrfToken(identity.csrf_token)
      await load()
      navigate(identity.role === 'admin' ? '/admin' : '/op', { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Sign-in failed',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-sm px-5 py-14">
      <p className="eyebrow">Staff</p>
      <h1 className="mt-4 text-3xl">Sign in</h1>

      <form onSubmit={onSubmit} className="mt-8 grid gap-4">
        <label className="grid gap-1.5">
          <span className="text-sm font-semibold text-ink">Username</span>
          <input
            name="username"
            autoComplete="username"
            required
            className="rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
          />
        </label>

        <label className="grid gap-1.5">
          <span className="text-sm font-semibold text-ink">Password</span>
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            className="rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
          />
        </label>

        <label className="grid gap-1.5">
          <span className="text-sm font-semibold text-ink">
            Authenticator code <span className="font-normal text-muted">(if enabled)</span>
          </span>
          <input
            name="totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            className="rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input name="remember" type="checkbox" /> Remember this device for 30 days
        </label>

        {error ? (
          <p role="alert" className="m-0 rounded-btn bg-blue-tint px-3.5 py-2.5 text-sm text-ink">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className="rounded-btn bg-blue px-6 py-3 font-bold text-white disabled:opacity-60"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
